import React from 'react';
import { Box, Chip } from '@mui/material';
import DragIndicator from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface SortableChipListProps {
  items: string[];
  onReorder: (newOrder: string[]) => void;
  disabled?: boolean;
  chipSize?: 'small' | 'medium';
}

function SortableChip({
  id,
  label,
  disabled,
  size,
}: {
  id: string;
  label: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <Chip
        size={size ?? 'small'}
        label={label}
        sx={{
          cursor: disabled ? 'default' : 'grab',
          '&:active': { cursor: disabled ? 'default' : 'grabbing' },
          pr: 0.5,
        }}
        icon={
          disabled ? undefined : (
            <Box
              component="span"
              data-drag-handle
              sx={{ display: 'flex', alignItems: 'center', mr: 0.25 }}
              {...listeners}
              {...attributes}
              onClick={e => e.stopPropagation()}
              onMouseDown={e => e.stopPropagation()}
            >
              <DragIndicator sx={{ fontSize: 16 }} color="action" />
            </Box>
          )
        }
      />
    </Box>
  );
}

export function SortableChipList({
  items,
  onReorder,
  disabled,
  chipSize = 'small',
}: SortableChipListProps) {
  const sortableIds = items.map((_, i) => `chip-${i}`);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sortableIds.indexOf(String(active.id));
    const newIndex = sortableIds.indexOf(String(over.id));
    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(items, oldIndex, newIndex);
      onReorder(newOrder);
    }
  };

  if (items.length === 0) return null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortableIds}
        strategy={horizontalListSortingStrategy}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            alignItems: 'center',
          }}
        >
          {items.map((value, i) => (
            <SortableChip
              key={`${value}-${i}`}
              id={sortableIds[i]}
              label={value}
              disabled={disabled}
              size={chipSize}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
}
